import React from "react";
import { Box, IconButton, Stack, Typography } from "@mui/material";
import { PrintOutlined } from "@mui/icons-material";
import { toast } from "react-toastify";

import AppTableForPrint from "components/Shared/AppTable/AppTableForPrint";
import RowsSkeleton from "components/Shared/Skeleton/RowsSkeleton";
import {
  usePrintShipmentRefundCreditNoteMutation,
} from "store/services/PaymentsApi/shipmentRefundApi";

const EBillRefundTab = ({ ebills = [], isLoading, isFetching }) => {
  const [printShipmentRefundCreditNote, { isLoading: isPrintingCreditNote }] =
    usePrintShipmentRefundCreditNoteMutation();
  const isPrinting = isPrintingCreditNote;

  const handlePrint = async (row) => {
    if (!row?.eBillId && !row?._id) {
      return;
    }

    try {
      const response = await printShipmentRefundCreditNote({
        // Use the refund ID when the credit note is still pending; the API
        // creates it before printing/downloading.
        creditNoteId: row.eBillId || row._id,
      }).unwrap();

      const printUrl =
        response?.eBillPrintUrl ||
        response?.data?.print_url ||
        response?.data?.printUrl ||
        response?.data?.url ||
        (typeof response?.data === "string" ? response.data : null);

      if (printUrl) {
        const proxyUrl = `/api/proxy/download?url=${encodeURIComponent(
          printUrl,
        )}&filename=${encodeURIComponent(
          `shipment-refund-credit-note-${row.eBillInvoiceNo || row.eBillId}.pdf`,
        )}`;
        const fileResponse = await fetch(proxyUrl);
        if (!fileResponse.ok) {
          throw new Error("Unable to download credit note");
        }

        const fileBlob = await fileResponse.blob();
        const objectUrl = URL.createObjectURL(fileBlob);
        const link = document.createElement("a");
        link.href = objectUrl;
        link.download = `shipment-refund-credit-note-${row.eBillInvoiceNo || row.eBillId}.pdf`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(objectUrl);
      }
    } catch (error) {
      toast.error(
        error?.data?.message || error?.message || "Credit note could not be created or downloaded"
      );
    }
  };

  const columns = [
    {
      name: "Shipment ID",
      selector: (row) => row?.shipment?.shipmentId || "N/A",
      cell: (row) => row?.shipment?.shipmentId || "N/A",
      minWidth: "180px",
    },
    {
      name: "Amount",
      selector: (row) => row?.amount,
      cell: (row) => (row?.amount != null ? Number(row.amount).toFixed(2) : "0.00"),
      minWidth: "120px",
    },
    {
      name: "eBill Invoice No",
      selector: (row) => row?.eBillInvoiceNo || "N/A",
      cell: (row) => row?.eBillInvoiceNo || "N/A",
      minWidth: "180px",
    },
    {
      name: "Action",
      cell: (row) => (
        <IconButton
          size="small"
          aria-label="Print eBill"
          onClick={() => handlePrint(row)}
          disabled={
            isPrinting
          }
        >
          <PrintOutlined color="primary" />
        </IconButton>
      ),
      ignoreRowClick: true,
      right: true,
      minWidth: "120px",
    },
  ];

  if (isLoading || isFetching) {
    return <RowsSkeleton />;
  }

  if (!Array.isArray(ebills) || ebills.length === 0) {
    return (
      <Box px={3} py={4}>
        <Stack spacing={1} alignItems="center">
          <Typography variant="h6" color="text.secondary">
            No eBill details available
          </Typography>
        </Stack>
      </Box>
    );
  }

  return (
    <Box px={2} py={2}>
      <AppTableForPrint columns={columns} data={ebills} />
    </Box>
  );
};

export default EBillRefundTab;
